import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Button,
  Container,
  Dropdown,
  Header,
  Icon,
  Input,
  Label,
  Message,
  Modal,
  Segment,
  Table,
} from "semantic-ui-react";

import {
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";

import { db } from "../firebase/firebaseConfig";
import { UserContext } from "../context/UserContext";
import ExportarExcel from "../funciones/ExportarExcel";

// ============================================================
// COMPONENTE
// ============================================================

const UsuariosGlobales = () => {
  const { users } = useContext(UserContext);

  // ==========================================================
  // DATOS
  // ==========================================================

  const [usuarios, setUsuarios] = useState([]);
  const [empresasMap, setEmpresasMap] = useState({});
  const [sesionesMap, setSesionesMap] = useState({});

  // ==========================================================
  // LOADING
  // ==========================================================

  const [loadingUsuarios, setLoadingUsuarios] = useState(true);
  const [loadingEmpresas, setLoadingEmpresas] = useState(true);
  const [loadingSesiones, setLoadingSesiones] = useState(true);

  // ==========================================================
  // FILTROS
  // ==========================================================

  const [texto, setTexto] = useState("");
  const [filtroEmpresa, setFiltroEmpresa] = useState("");
  const [filtroRol, setFiltroRol] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("TODOS");
  const [filtroSesion, setFiltroSesion] = useState("TODAS");

  // ==========================================================
  // ACCIONES
  // ==========================================================

  const [modalOpen, setModalOpen] = useState(false);
  const [accionModal, setAccionModal] = useState("");
  const [usuarioSeleccionado, setUsuarioSeleccionado] =
    useState(null);
  const [sesionSeleccionada, setSesionSeleccionada] =
    useState(null);

  const [procesando, setProcesando] = useState(false);

  // ==========================================================
  // MENSAJES
  // ==========================================================

  const [mensaje, setMensaje] = useState(null);

  // ==========================================================
  // LOADING GENERAL
  // ==========================================================

  const loading =
    loadingUsuarios ||
    loadingEmpresas ||
    loadingSesiones;

  // ==========================================================
  // USUARIO ADMINISTRADOR ACTUAL
  // ==========================================================

  const correoAdmin = useMemo(() => {
    return String(
      users?.correo ||
        users?.email ||
        ""
    )
      .trim()
      .toLowerCase();
  }, [users]);

  // ==========================================================
  // CARGAR EMPRESAS
  // ==========================================================

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "empresas"),

      (snapshot) => {
        const map = {};

        snapshot.docs.forEach((documento) => {
          const data = documento.data();

          map[documento.id] =
            data?.empresa ||
            data?.nombre ||
            "Empresa";
        });

        setEmpresasMap(map);
        setLoadingEmpresas(false);
      },

      (error) => {
        console.error(
          "Error cargando empresas:",
          error
        );

        setLoadingEmpresas(false);

        setMensaje({
          tipo: "error",
          texto:
            "No fue posible cargar las empresas.",
        });
      }
    );

    return () => unsubscribe();
  }, []);

  // ==========================================================
  // CARGAR SESIONES
  // ==========================================================

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(
        db,
        "sesionesUsuarios"
      ),

      (snapshot) => {
        const map = {};

        snapshot.docs.forEach((documento) => {
          const data = documento.data();

          /*
           * Normalmente sesionesUsuarios/{uid}
           * utiliza el UID como id del documento.
           *
           * También soportamos documentos que
           * tengan un campo uid explícito.
           */
          const uid =
            data?.uid ||
            documento.id;

          map[uid] = {
            id: documento.id,
            ...data,
          };
        });

        setSesionesMap(map);
        setLoadingSesiones(false);
      },

      (error) => {
        console.error(
          "Error cargando sesiones:",
          error
        );

        setLoadingSesiones(false);

        setMensaje({
          tipo: "error",
          texto:
            "No fue posible cargar las sesiones de usuarios.",
        });
      }
    );

    return () => unsubscribe();
  }, []);

  // ==========================================================
  // CARGAR USUARIOS
  // ==========================================================

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "usuarios"),

      (snapshot) => {
        const data = snapshot.docs
          .map((documento) => ({
            id: documento.id,
            ...documento.data(),
          }))
          .sort((a, b) => {
            const empresaA =
              empresasMap[
                a.emp_id
              ] || "";

            const empresaB =
              empresasMap[
                b.emp_id
              ] || "";

            const comparacionEmpresa =
              empresaA.localeCompare(
                empresaB,
                "es",
                {
                  sensitivity:
                    "base",
                }
              );

            if (
              comparacionEmpresa !==
              0
            ) {
              return comparacionEmpresa;
            }

            const nombreA =
              `${a.nombre || ""} ${
                a.apellido || ""
              }`;

            const nombreB =
              `${b.nombre || ""} ${
                b.apellido || ""
              }`;

            return nombreA.localeCompare(
              nombreB,
              "es",
              {
                sensitivity:
                  "base",
              }
            );
          });

        setUsuarios(data);
        setLoadingUsuarios(false);
      },

      (error) => {
        console.error(
          "Error cargando usuarios:",
          error
        );

        setLoadingUsuarios(false);

        setMensaje({
          tipo: "error",
          texto:
            "No fue posible cargar los usuarios.",
        });
      }
    );

    return () => unsubscribe();
  }, [empresasMap]);

  // ==========================================================
  // OBTENER UID DEL USUARIO
  // ==========================================================

  const obtenerUid = (usuario) => {
    return (
      usuario?.uid ||
      usuario?.id ||
      ""
    );
  };

  // ==========================================================
  // OBTENER SESIÓN
  // ==========================================================

  const obtenerSesion = (usuario) => {
    const uid =
      obtenerUid(usuario);

    if (!uid) {
      return null;
    }

    return (
      sesionesMap[uid] ||
      null
    );
  };

  // ==========================================================
  // FORMATEAR FECHA
  // ==========================================================

  const formatoFecha = (fecha) => {
    if (!fecha) {
      return "-";
    }

    try {
      let date;

      if (
        typeof fecha.toDate ===
        "function"
      ) {
        date = fecha.toDate();
      } else if (
        fecha instanceof Date
      ) {
        date = fecha;
      } else {
        date = new Date(fecha);
      }

      if (
        Number.isNaN(
          date.getTime()
        )
      ) {
        return "-";
      }

      return date.toLocaleString(
        "es-CL",
        {
          dateStyle: "short",
          timeStyle: "short",
        }
      );
    } catch (error) {
      return "-";
    }
  };

  // ==========================================================
  // OPCIONES EMPRESA
  // ==========================================================

  const empresaOptions = useMemo(() => {
    return Object.entries(
      empresasMap
    )
      .map(
        ([
          id,
          nombre,
        ]) => ({
          key: id,
          text: nombre,
          value: id,
        })
      )
      .sort((a, b) =>
        a.text.localeCompare(
          b.text,
          "es",
          {
            sensitivity:
              "base",
          }
        )
      );
  }, [empresasMap]);

  // ==========================================================
  // OPCIONES ROLES
  // ==========================================================

  const roleOptions = useMemo(() => {
    const roles = [
      ...new Set(
        usuarios
          .map((usuario) =>
            String(
              usuario.rol ||
                ""
            ).trim()
          )
          .filter(Boolean)
      ),
    ];

    return roles
      .sort((a, b) =>
        a.localeCompare(
          b,
          "es",
          {
            sensitivity:
              "base",
          }
        )
      )
      .map((rol) => ({
        key: rol,
        text: rol,
        value: rol,
      }));
  }, [usuarios]);

  // ==========================================================
  // USUARIOS FILTRADOS
  // ==========================================================

  const usuariosFiltrados =
    useMemo(() => {
      const textoBusqueda =
        texto
          .trim()
          .toLowerCase();

      return usuarios
        .filter((usuario) => {
          const uid =
            obtenerUid(
              usuario
            );

          const sesion =
            sesionesMap[uid];

          const empresa =
            empresasMap[
              usuario.emp_id
            ] || "";

          const nombreCompleto =
            `${usuario.nombre || ""} ${
              usuario.apellido || ""
            }`.trim();

          // ================================================
          // TEXTO
          // ================================================

          const contenido =
            [
              empresa,
              nombreCompleto,
              usuario.correo,
              usuario.rol,
              usuario.emp_id,
              sesion?.dispositivo,
            ]
              .join(" ")
              .toLowerCase();

          const coincideTexto =
            !textoBusqueda ||
            contenido.includes(
              textoBusqueda
            );

          // ================================================
          // EMPRESA
          // ================================================

          const coincideEmpresa =
            !filtroEmpresa ||
            usuario.emp_id ===
              filtroEmpresa;

          // ================================================
          // ROL
          // ================================================

          const coincideRol =
            !filtroRol ||
            usuario.rol ===
              filtroRol;

          // ================================================
          // ESTADO USUARIO
          // ================================================

          const coincideEstado =
            filtroEstado ===
              "TODOS" ||
            (filtroEstado ===
              "ACTIVOS" &&
              usuario.activo !==
                false) ||
            (filtroEstado ===
              "BLOQUEADOS" &&
              usuario.activo ===
                false);

          // ================================================
          // SESIÓN
          // ================================================

          const conectado =
            sesion?.activa ===
            true;

          const coincideSesion =
            filtroSesion ===
              "TODAS" ||
            (filtroSesion ===
              "CONECTADOS" &&
              conectado) ||
            (filtroSesion ===
              "OFFLINE" &&
              !conectado);

          return (
            coincideTexto &&
            coincideEmpresa &&
            coincideRol &&
            coincideEstado &&
            coincideSesion
          );
        })
        .sort((a, b) => {
          const empresaA =
            empresasMap[
              a.emp_id
            ] || "";

          const empresaB =
            empresasMap[
              b.emp_id
            ] || "";

          const comparacionEmpresa =
            empresaA.localeCompare(
              empresaB,
              "es",
              {
                sensitivity:
                  "base",
              }
            );

          if (
            comparacionEmpresa !==
            0
          ) {
            return comparacionEmpresa;
          }

          const nombreA =
            `${a.nombre || ""} ${
              a.apellido || ""
            }`;

          const nombreB =
            `${b.nombre || ""} ${
              b.apellido || ""
            }`;

          return nombreA.localeCompare(
            nombreB,
            "es",
            {
              sensitivity:
                "base",
            }
          );
        });
    }, [
      usuarios,
      empresasMap,
      sesionesMap,
      texto,
      filtroEmpresa,
      filtroRol,
      filtroEstado,
      filtroSesion,
    ]);

  // ==========================================================
  // KPIs
  // ==========================================================

  const totalUsuarios =
    usuarios.length;

  const totalActivos =
    useMemo(() => {
      return usuarios.filter(
        (usuario) =>
          usuario.activo !==
          false
      ).length;
    }, [usuarios]);

  const totalBloqueados =
    useMemo(() => {
      return usuarios.filter(
        (usuario) =>
          usuario.activo ===
          false
      ).length;
    }, [usuarios]);

  /*
   * Contamos usuarios existentes que tengan
   * una sesión activa.
   *
   * No contamos sesiones huérfanas.
   */
  const totalConectados =
    useMemo(() => {
      return usuarios.filter(
        (usuario) => {
          const sesion =
            sesionesMap[
              obtenerUid(
                usuario
              )
            ];

          return (
            sesion?.activa ===
            true
          );
        }
      ).length;
    }, [
      usuarios,
      sesionesMap,
    ]);

  // ==========================================================
  // FILTROS ACTIVOS
  // ==========================================================

  const hayFiltros =
    Boolean(texto) ||
    Boolean(filtroEmpresa) ||
    Boolean(filtroRol) ||
    filtroEstado !==
      "TODOS" ||
    filtroSesion !==
      "TODAS";

  // ==========================================================
  // LIMPIAR FILTROS
  // ==========================================================

  const limpiarFiltros =
    () => {
      setTexto("");
      setFiltroEmpresa("");
      setFiltroRol("");
      setFiltroEstado(
        "TODOS"
      );
      setFiltroSesion(
        "TODAS"
      );
    };

  // ==========================================================
  // VERIFICAR SI ES EL MISMO ADMIN
  // ==========================================================

  const esUsuarioActual = (
    usuario
  ) => {
    const correoUsuario =
      String(
        usuario?.correo ||
          ""
      )
        .trim()
        .toLowerCase();

    if (
      !correoAdmin ||
      !correoUsuario
    ) {
      return false;
    }

    return (
      correoAdmin ===
      correoUsuario
    );
  };

  // ==========================================================
  // ABRIR MODAL
  // ==========================================================

  const abrirModal = (
    tipo,
    usuario
  ) => {
    const sesion =
      obtenerSesion(usuario);

    setAccionModal(tipo);

    setUsuarioSeleccionado(
      usuario
    );

    setSesionSeleccionada(
      sesion
    );

    setModalOpen(true);
  };

  // ==========================================================
  // CERRAR MODAL
  // ==========================================================

  const cerrarModal = () => {
    if (procesando) {
      return;
    }

    setModalOpen(false);
    setAccionModal("");
    setUsuarioSeleccionado(
      null
    );
    setSesionSeleccionada(
      null
    );
  };

  // ==========================================================
  // EJECUTAR ACCIÓN
  // ==========================================================

  const ejecutarAccion =
    async () => {
      if (
        !usuarioSeleccionado ||
        procesando
      ) {
        return;
      }

      setProcesando(true);
      setMensaje(null);

      try {
        const batch =
          writeBatch(db);

        const usuarioRef = doc(
          db,
          "usuarios",
          usuarioSeleccionado.id
        );

        const usuarioAdmin =
          users?.correo ||
          users?.email ||
          "sistema";

        // ====================================================
        // CERRAR SESIÓN
        // ====================================================

        if (
          accionModal ===
          "CERRAR_SESION"
        ) {
          if (
            !sesionSeleccionada?.id
          ) {
            throw new Error(
              "SESION_NO_ENCONTRADA"
            );
          }

          const sesionRef =
            doc(
              db,
              "sesionesUsuarios",
              sesionSeleccionada.id
            );

          batch.update(
            sesionRef,
            {
              activa: false,

              cerradaPorSistema:
                true,

              motivoCierre:
                "Cierre forzado desde BackOffice",

              cerradoPor:
                usuarioAdmin,

              fechaCierre:
                serverTimestamp(),

              fechamod:
                serverTimestamp(),
            }
          );
        }

        // ====================================================
        // BLOQUEAR
        // ====================================================

        if (
          accionModal ===
          "BLOQUEAR"
        ) {
          if (
            esUsuarioActual(
              usuarioSeleccionado
            )
          ) {
            throw new Error(
              "NO_BLOQUEAR_SELF"
            );
          }

          batch.update(
            usuarioRef,
            {
              activo: false,

              user_mod:
                usuarioAdmin,

              fecha_mod:
                serverTimestamp(),
            }
          );

          /*
           * Si el usuario está conectado,
           * bloquear también termina su
           * sesión en la misma operación.
           */
          if (
            sesionSeleccionada?.activa ===
              true &&
            sesionSeleccionada?.id
          ) {
            const sesionRef =
              doc(
                db,
                "sesionesUsuarios",
                sesionSeleccionada.id
              );

            batch.update(
              sesionRef,
              {
                activa: false,

                cerradaPorSistema:
                  true,

                motivoCierre:
                  "Usuario bloqueado desde BackOffice",

                cerradoPor:
                  usuarioAdmin,

                fechaCierre:
                  serverTimestamp(),

                fechamod:
                  serverTimestamp(),
              }
            );
          }
        }

        // ====================================================
        // DESBLOQUEAR
        // ====================================================

        if (
          accionModal ===
          "DESBLOQUEAR"
        ) {
          batch.update(
            usuarioRef,
            {
              activo: true,

              user_mod:
                usuarioAdmin,

              fecha_mod:
                serverTimestamp(),
            }
          );
        }

        // ====================================================
        // COMMIT
        // ====================================================

        await batch.commit();

        if (
          accionModal ===
          "CERRAR_SESION"
        ) {
          setMensaje({
            tipo: "success",
            texto:
              "La sesión del usuario fue cerrada correctamente.",
          });
        }

        if (
          accionModal ===
          "BLOQUEAR"
        ) {
          setMensaje({
            tipo: "success",
            texto:
              "El usuario fue bloqueado correctamente.",
          });
        }

        if (
          accionModal ===
          "DESBLOQUEAR"
        ) {
          setMensaje({
            tipo: "success",
            texto:
              "El usuario fue desbloqueado correctamente.",
          });
        }

        setModalOpen(false);
        setAccionModal("");
        setUsuarioSeleccionado(
          null
        );
        setSesionSeleccionada(
          null
        );
      } catch (error) {
        console.error(
          "Error ejecutando acción:",
          error
        );

        let textoError =
          "No fue posible completar la operación.";

        if (
          error.message ===
          "SESION_NO_ENCONTRADA"
        ) {
          textoError =
            "No se encontró una sesión activa para este usuario.";
        }

        if (
          error.message ===
          "NO_BLOQUEAR_SELF"
        ) {
          textoError =
            "No puedes bloquear tu propio usuario desde esta pantalla.";
        }

        setMensaje({
          tipo: "error",
          texto:
            textoError,
        });
      } finally {
        setProcesando(false);
      }
    };

  // ==========================================================
  // EXPORTAR EXCEL
  // ==========================================================

  const exportarExcel =
    () => {
      if (
        usuariosFiltrados.length ===
        0
      ) {
        return;
      }

      const rows =
        usuariosFiltrados.map(
          (usuario) => {
            const sesion =
              obtenerSesion(
                usuario
              );

            return {
              Empresa:
                empresasMap[
                  usuario.emp_id
                ] || "",

              Nombre:
                usuario.nombre ||
                "",

              Apellido:
                usuario.apellido ||
                "",

              Correo:
                usuario.correo ||
                "",

              Rol:
                usuario.rol ||
                "",

              Estado:
                usuario.activo ===
                false
                  ? "BLOQUEADO"
                  : "ACTIVO",

              Sesion:
                sesion?.activa ===
                true
                  ? "CONECTADO"
                  : "OFFLINE",

              UltimaActividad:
                formatoFecha(
                  sesion?.lastActive
                ),

              Dispositivo:
                sesion?.dispositivo ||
                "",
            };
          }
        );

      const columnsToShow = [
        "Empresa",
        "Nombre",
        "Apellido",
        "Correo",
        "Rol",
        "Estado",
        "Sesion",
        "UltimaActividad",
        "Dispositivo",
      ];

      const excelBlob =
        ExportarExcel(
          rows,
          columnsToShow
        );

      const excelURL =
        URL.createObjectURL(
          excelBlob
        );

      const link =
        document.createElement(
          "a"
        );

      link.href =
        excelURL;

      link.download =
        "usuarios_plataforma.xlsx";

      document.body.appendChild(
        link
      );

      link.click();

      document.body.removeChild(
        link
      );

      URL.revokeObjectURL(
        excelURL
      );
    };

  // ==========================================================
  // DATOS MODAL
  // ==========================================================

  const tituloModal =
    useMemo(() => {
      if (
        accionModal ===
        "CERRAR_SESION"
      ) {
        return "Cerrar sesión";
      }

      if (
        accionModal ===
        "BLOQUEAR"
      ) {
        return "Bloquear usuario";
      }

      if (
        accionModal ===
        "DESBLOQUEAR"
      ) {
        return "Desbloquear usuario";
      }

      return "";
    }, [accionModal]);

  const iconoModal =
    useMemo(() => {
      if (
        accionModal ===
        "CERRAR_SESION"
      ) {
        return "sign-out";
      }

      if (
        accionModal ===
        "BLOQUEAR"
      ) {
        return "ban";
      }

      if (
        accionModal ===
        "DESBLOQUEAR"
      ) {
        return "unlock";
      }

      return "question";
    }, [accionModal]);

  const colorModal =
    useMemo(() => {
      if (
        accionModal ===
        "DESBLOQUEAR"
      ) {
        return "#21ba45";
      }

      if (
        accionModal ===
        "CERRAR_SESION"
      ) {
        return "#f2711c";
      }

      return "#db2828";
    }, [accionModal]);

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <Container
      fluid
      style={{
        maxWidth: 1500,
        margin: "0 auto",
        padding:
          "24px 28px",
        minHeight:
          "100vh",
        background:
          "#f8fafc",
      }}
    >
      {/* ==================================================== */}
      {/* HEADER */}
      {/* ==================================================== */}

      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems:
            "center",
          gap: 20,
          flexWrap:
            "wrap",
          marginBottom: 20,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems:
              "center",
            gap: 14,
          }}
        >
          <div
            style={{
              width: 48,
              height: 48,
              minWidth: 48,
              borderRadius: 12,
              background:
                "#e5f7f7",
              display: "flex",
              alignItems:
                "center",
              justifyContent:
                "center",
            }}
          >
            <Icon
              name="users"
              style={{
                margin: 0,
                color:
                  "#0d9da5",
                fontSize: 21,
              }}
            />
          </div>

          <div>
            <Header
              as="h2"
              style={{
                margin: 0,
                color:
                  "#172b4d",
                fontWeight:
                  700,
              }}
            >
              Usuarios de la Plataforma
            </Header>

            <div
              style={{
                color:
                  "#8793a5",
                fontSize: 13,
                marginTop: 4,
              }}
            >
              Administración y supervisión global de usuarios y sesiones
            </div>
          </div>
        </div>

        <Button
          type="button"
          onClick={
            exportarExcel
          }
          disabled={
            usuariosFiltrados.length ===
            0
          }
          style={{
            background:
              "#ffffff",
            color:
              "#526273",
            border:
              "1px solid #d7dee7",
            borderRadius: 6,
            boxShadow:
              "none",
          }}
        >
          <Icon
            name="file excel outline"
            style={{
              color:
                "#21ba45",
            }}
          />

          Exportar Excel
        </Button>
      </div>

      {/* ==================================================== */}
      {/* MENSAJES */}
      {/* ==================================================== */}

      {mensaje && (
        <Message
          positive={
            mensaje.tipo ===
            "success"
          }
          negative={
            mensaje.tipo ===
            "error"
          }
          onDismiss={() =>
            setMensaje(null)
          }
          style={{
            boxShadow:
              "none",
            borderRadius: 7,
          }}
        >
          <Icon
            name={
              mensaje.tipo ===
              "success"
                ? "check circle"
                : "warning sign"
            }
          />

          {mensaje.texto}
        </Message>
      )}

      {/* ==================================================== */}
      {/* KPI */}
      {/* ==================================================== */}

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(210px, 1fr))",
          gap: 12,
          marginBottom: 14,
        }}
      >
        <ResumenCard
          titulo="USUARIOS"
          valor={totalUsuarios}
          descripcion="Registrados en la plataforma"
          icon="users"
          colorIcono="#2185d0"
          fondoIcono="#e9f3fb"
        />

        <ResumenCard
          titulo="ACTIVOS"
          valor={totalActivos}
          descripcion="Usuarios habilitados"
          icon="check circle"
          colorIcono="#21ba45"
          fondoIcono="#eaf8ee"
        />

        <ResumenCard
          titulo="CONECTADOS"
          valor={
            totalConectados
          }
          descripcion="Con sesión activa"
          icon="wifi"
          colorIcono="#0d9da5"
          fondoIcono="#e5f7f7"
        />

        <ResumenCard
          titulo="BLOQUEADOS"
          valor={
            totalBloqueados
          }
          descripcion="Usuarios sin acceso"
          icon="ban"
          colorIcono="#db2828"
          fondoIcono="#fdecec"
        />
      </div>

      {/* ==================================================== */}
      {/* FILTROS */}
      {/* ==================================================== */}

      <Segment
        style={{
          padding: 0,
          borderRadius: 8,
          border:
            "1px solid #e5e9ef",
          boxShadow:
            "0 2px 6px rgba(0,0,0,0.04)",
          overflow:
            "visible",
          marginBottom: 14,
        }}
      >
        <div
          style={{
            padding:
              "14px 18px",
            borderBottom:
              "1px solid #edf0f4",
            display: "flex",
            alignItems:
              "center",
            gap: 8,
            color:
              "#253858",
            fontWeight: 600,
          }}
        >
          <Icon
            name="filter"
            style={{
              color:
                "#0d9da5",
            }}
          />

          Buscar y filtrar
        </div>

        <div
          style={{
            padding: 16,
            display: "grid",
            gridTemplateColumns:
              "minmax(280px, 2fr) repeat(4, minmax(150px, 1fr)) auto",
            gap: 10,
            alignItems:
              "center",
          }}
        >
          {/* BUSCADOR */}

          <Input
            fluid
            placeholder="Buscar usuario, empresa, correo, rol o dispositivo..."
            value={texto}
            onChange={(e) =>
              setTexto(
                e.target.value
              )
            }
            icon={{
              name:
                "search",
              color:
                "teal",
            }}
            iconPosition="left"
            action={
              texto
                ? {
                    icon:
                      "close",
                    onClick:
                      () =>
                        setTexto(
                          ""
                        ),
                    title:
                      "Limpiar búsqueda",
                  }
                : undefined
            }
          />

          {/* EMPRESA */}

          <Dropdown
            fluid
            search
            selection
            clearable
            placeholder="Empresa"
            value={
              filtroEmpresa
            }
            onChange={(
              e,
              { value }
            ) =>
              setFiltroEmpresa(
                value || ""
              )
            }
            options={
              empresaOptions
            }
          />

          {/* ROL */}

          <Dropdown
            fluid
            search
            selection
            clearable
            placeholder="Rol"
            value={
              filtroRol
            }
            onChange={(
              e,
              { value }
            ) =>
              setFiltroRol(
                value || ""
              )
            }
            options={
              roleOptions
            }
          />

          {/* ESTADO */}

          <Dropdown
            fluid
            selection
            value={
              filtroEstado
            }
            onChange={(
              e,
              { value }
            ) =>
              setFiltroEstado(
                value
              )
            }
            options={[
              {
                key:
                  "TODOS",
                text:
                  "Todos los estados",
                value:
                  "TODOS",
              },
              {
                key:
                  "ACTIVOS",
                text:
                  "Activos",
                value:
                  "ACTIVOS",
              },
              {
                key:
                  "BLOQUEADOS",
                text:
                  "Bloqueados",
                value:
                  "BLOQUEADOS",
              },
            ]}
          />

          {/* SESIÓN */}

          <Dropdown
            fluid
            selection
            value={
              filtroSesion
            }
            onChange={(
              e,
              { value }
            ) =>
              setFiltroSesion(
                value
              )
            }
            options={[
              {
                key:
                  "TODAS",
                text:
                  "Todas las sesiones",
                value:
                  "TODAS",
              },
              {
                key:
                  "CONECTADOS",
                text:
                  "Conectados",
                value:
                  "CONECTADOS",
              },
              {
                key:
                  "OFFLINE",
                text:
                  "Offline",
                value:
                  "OFFLINE",
              },
            ]}
          />

          {/* LIMPIAR */}

          <Button
            type="button"
            icon
            title="Limpiar filtros"
            disabled={
              !hayFiltros
            }
            onClick={
              limpiarFiltros
            }
            style={{
              background:
                "#ffffff",
              border:
                "1px solid #d7dee7",
              boxShadow:
                "none",
            }}
          >
            <Icon
              name="close"
              style={{
                color:
                  "#767676",
              }}
            />
          </Button>
        </div>
      </Segment>

      {/* ==================================================== */}
      {/* TABLA */}
      {/* ==================================================== */}

      <Segment
        loading={loading}
        style={{
          padding: 0,
          borderRadius: 8,
          border:
            "1px solid #e5e9ef",
          boxShadow:
            "0 2px 6px rgba(0,0,0,0.04)",
          overflow:
            "hidden",
        }}
      >
        {/* HEADER TABLA */}

        <div
          style={{
            padding:
              "14px 18px",
            borderBottom:
              "1px solid #edf0f4",
            display: "flex",
            justifyContent:
              "space-between",
            alignItems:
              "center",
            gap: 12,
            flexWrap:
              "wrap",
          }}
        >
          <div
            style={{
              display:
                "flex",
              alignItems:
                "center",
              gap: 8,
              color:
                "#253858",
              fontWeight: 600,
            }}
          >
            <Icon
              name="users"
              style={{
                color:
                  "#0d9da5",
              }}
            />

            Usuarios registrados
          </div>

          <div
            style={{
              fontSize: 12,
              color:
                "#8793a5",
            }}
          >
            Mostrando{" "}
            <strong>
              {
                usuariosFiltrados.length
              }
            </strong>{" "}
            de{" "}
            <strong>
              {usuarios.length}
            </strong>
          </div>
        </div>

        {/* EMPTY */}

        {!loading &&
          usuariosFiltrados.length ===
            0 && (
            <div
              style={{
                padding: 38,
                textAlign:
                  "center",
                color:
                  "#8793a5",
              }}
            >
              <Icon
                name="search"
                style={{
                  display:
                    "block",
                  margin:
                    "0 auto 10px",
                  fontSize: 22,
                  color:
                    "#2185d0",
                }}
              />

              No se encontraron usuarios con los filtros seleccionados.
            </div>
          )}

        {/* TABLE */}

        {usuariosFiltrados.length >
          0 && (
          <div
            style={{
              overflowX:
                "auto",
              maxHeight: 600,
              overflowY:
                "auto",
            }}
          >
            <Table
              celled
              compact
              selectable
              unstackable
              style={{
                margin: 0,
                border: 0,
                borderRadius: 0,
                minWidth: 1450,
                fontSize: 12,
              }}
            >
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell
                    style={
                      stickyTh
                    }
                  >
                    Empresa
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    style={
                      stickyTh
                    }
                  >
                    Usuario
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    style={
                      stickyTh
                    }
                  >
                    Correo
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    style={
                      stickyTh
                    }
                  >
                    Rol
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    textAlign="center"
                    style={
                      stickyTh
                    }
                  >
                    Estado
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    textAlign="center"
                    style={
                      stickyTh
                    }
                  >
                    Sesión
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    style={
                      stickyTh
                    }
                  >
                    Última actividad
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    style={
                      stickyTh
                    }
                  >
                    Dispositivo
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    textAlign="center"
                    style={
                      stickyTh
                    }
                  >
                    Acciones
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {usuariosFiltrados.map(
                  (usuario) => {
                    const sesion =
                      obtenerSesion(
                        usuario
                      );

                    const conectado =
                      sesion?.activa ===
                      true;

                    const activo =
                      usuario.activo !==
                      false;

                    const usuarioActual =
                      esUsuarioActual(
                        usuario
                      );

                    return (
                      <Table.Row
                        key={
                          usuario.id
                        }
                      >
                        {/* EMPRESA */}

                        <Table.Cell>
                          <div
                            style={{
                              display:
                                "flex",
                              alignItems:
                                "center",
                              gap: 7,
                            }}
                          >
                            <Icon
                              name="building outline"
                              style={{
                                color:
                                  "#2185d0",
                                margin: 0,
                              }}
                            />

                            <strong
                              style={{
                                color:
                                  "#344563",
                              }}
                            >
                              {empresasMap[
                                usuario
                                  .emp_id
                              ] ||
                                usuario.empresa ||
                                "-"}
                            </strong>
                          </div>
                        </Table.Cell>

                        {/* USUARIO */}

                        <Table.Cell>
                          <div
                            style={{
                              display:
                                "flex",
                              alignItems:
                                "center",
                              gap: 7,
                            }}
                          >
                            <Icon
                              name="user outline"
                              style={{
                                color:
                                  "#2185d0",
                                margin: 0,
                              }}
                            />

                            <span
                              style={{
                                fontWeight:
                                  600,
                                color:
                                  "#344563",
                              }}
                            >
                              {`${usuario.nombre || ""} ${
                                usuario.apellido || ""
                              }`.trim() ||
                                "-"}
                            </span>

                            {usuarioActual && (
                              <Label
                                basic
                                color="teal"
                                size="mini"
                                style={{
                                  marginLeft:
                                    4,
                                }}
                              >
                                Tú
                              </Label>
                            )}
                          </div>
                        </Table.Cell>

                        {/* CORREO */}

                        <Table.Cell>
                          <Icon
                            name="mail outline"
                            style={{
                              color:
                                "#6435c9",
                              marginRight:
                                7,
                            }}
                          />

                          {usuario.correo ||
                            "-"}
                        </Table.Cell>

                        {/* ROL */}

                        <Table.Cell>
                          <Icon
                            name="id badge outline"
                            style={{
                              color:
                                "#0d9da5",
                              marginRight:
                                7,
                            }}
                          />

                          <Label
                            basic
                            color="blue"
                            size="tiny"
                          >
                            {usuario.rol ||
                              "-"}
                          </Label>
                        </Table.Cell>

                        {/* ESTADO */}

                        <Table.Cell
                          textAlign="center"
                        >
                          {activo ? (
                            <Label
                              basic
                              color="green"
                              size="tiny"
                            >
                              <Icon
                                name="check circle"
                                style={{
                                  color:
                                    "#21ba45",
                                }}
                              />

                              Activo
                            </Label>
                          ) : (
                            <Label
                              basic
                              color="red"
                              size="tiny"
                            >
                              <Icon
                                name="ban"
                                style={{
                                  color:
                                    "#db2828",
                                }}
                              />

                              Bloqueado
                            </Label>
                          )}
                        </Table.Cell>

                        {/* SESIÓN */}

                        <Table.Cell
                          textAlign="center"
                        >
                          {conectado ? (
                            <Label
                              basic
                              color="green"
                              size="tiny"
                            >
                              <Icon
                                name="wifi"
                                style={{
                                  color:
                                    "#21ba45",
                                }}
                              />

                              Conectado
                            </Label>
                          ) : (
                            <Label
                              basic
                              color="grey"
                              size="tiny"
                            >
                              <Icon
                                name="circle outline"
                                style={{
                                  color:
                                    "#767676",
                                }}
                              />

                              Offline
                            </Label>
                          )}

                          {sesion?.cerradaPorSistema ===
                            true &&
                            !conectado && (
                              <Label
                                basic
                                color="red"
                                size="tiny"
                                style={{
                                  marginLeft:
                                    5,
                                }}
                              >
                                Forzada
                              </Label>
                            )}
                        </Table.Cell>

                        {/* ÚLTIMA ACTIVIDAD */}

                        <Table.Cell>
                          <Icon
                            name="clock outline"
                            style={{
                              color:
                                "#f2711c",
                              marginRight:
                                7,
                            }}
                          />

                          {formatoFecha(
                            sesion?.lastActive
                          )}
                        </Table.Cell>

                        {/* DISPOSITIVO */}

                        <Table.Cell>
                          {sesion?.dispositivo ? (
                            <div
                              title={
                                sesion.dispositivo
                              }
                              style={{
                                display:
                                  "flex",
                                alignItems:
                                  "center",
                                gap: 7,
                                maxWidth:
                                  230,
                              }}
                            >
                              <Icon
                                name="laptop"
                                style={{
                                  color:
                                    "#6435c9",
                                  margin: 0,
                                }}
                              />

                              <span
                                style={{
                                  overflow:
                                    "hidden",
                                  textOverflow:
                                    "ellipsis",
                                  whiteSpace:
                                    "nowrap",
                                }}
                              >
                                {
                                  sesion.dispositivo
                                }
                              </span>
                            </div>
                          ) : (
                            "-"
                          )}
                        </Table.Cell>

                        {/* ACCIONES */}

                        <Table.Cell
                          textAlign="center"
                        >
                          <div
                            style={{
                              display:
                                "flex",
                              justifyContent:
                                "center",
                              gap: 6,
                              flexWrap:
                                "wrap",
                            }}
                          >
                            {/* CERRAR SESIÓN */}

                            <Button
                              type="button"
                              icon
                              size="mini"
                              title="Cerrar sesión"
                              disabled={
                                !conectado
                              }
                              onClick={() =>
                                abrirModal(
                                  "CERRAR_SESION",
                                  usuario
                                )
                              }
                              style={{
                                background:
                                  conectado
                                    ? "#fff4e8"
                                    : "#f4f5f7",
                                color:
                                  conectado
                                    ? "#f2711c"
                                    : "#a5adba",
                                border:
                                  conectado
                                    ? "1px solid #f7c89b"
                                    : "1px solid #e5e9ef",
                                boxShadow:
                                  "none",
                              }}
                            >
                              <Icon
                                name="sign-out"
                                style={{
                                  margin: 0,
                                }}
                              />
                            </Button>

                            {/* BLOQUEAR / DESBLOQUEAR */}

                            {activo ? (
                              <Button
                                type="button"
                                icon
                                size="mini"
                                title={
                                  usuarioActual
                                    ? "No puedes bloquear tu propio usuario"
                                    : "Bloquear usuario"
                                }
                                disabled={
                                  usuarioActual
                                }
                                onClick={() =>
                                  abrirModal(
                                    "BLOQUEAR",
                                    usuario
                                  )
                                }
                                style={{
                                  background:
                                    "#fdecec",
                                  color:
                                    "#db2828",
                                  border:
                                    "1px solid #f2b8b5",
                                  boxShadow:
                                    "none",
                                }}
                              >
                                <Icon
                                  name="ban"
                                  style={{
                                    margin: 0,
                                  }}
                                />
                              </Button>
                            ) : (
                              <Button
                                type="button"
                                icon
                                size="mini"
                                title="Desbloquear usuario"
                                onClick={() =>
                                  abrirModal(
                                    "DESBLOQUEAR",
                                    usuario
                                  )
                                }
                                style={{
                                  background:
                                    "#eaf8ee",
                                  color:
                                    "#21ba45",
                                  border:
                                    "1px solid #b9e5c4",
                                  boxShadow:
                                    "none",
                                }}
                              >
                                <Icon
                                  name="unlock"
                                  style={{
                                    margin: 0,
                                  }}
                                />
                              </Button>
                            )}
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    );
                  }
                )}
              </Table.Body>
            </Table>
          </div>
        )}

        {/* FOOTER */}

        {usuariosFiltrados.length >
          0 && (
          <div
            style={{
              padding:
                "11px 18px",
              borderTop:
                "1px solid #edf0f4",
              background:
                "#fafbfc",
              color:
                "#697586",
              fontSize: 11,
            }}
          >
            <Icon
              name="info circle"
              style={{
                color:
                  "#2185d0",
              }}
            />

            Los cambios de usuario y sesión se actualizan en tiempo real.
          </div>
        )}
      </Segment>

      {/* ==================================================== */}
      {/* MODAL ACCIONES */}
      {/* ==================================================== */}

      <Modal
        size="tiny"
        open={modalOpen}
        onClose={
          cerrarModal
        }
        closeOnEscape={
          !procesando
        }
        closeOnDimmerClick={
          !procesando
        }
        style={{
          borderRadius: 8,
          overflow:
            "hidden",
        }}
      >
        <Modal.Header
          style={{
            padding:
              "16px 20px",
          }}
        >
          <div
            style={{
              display:
                "flex",
              alignItems:
                "center",
              gap: 11,
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 9,
                background:
                  `${colorModal}15`,
                display:
                  "flex",
                alignItems:
                  "center",
                justifyContent:
                  "center",
              }}
            >
              <Icon
                name={
                  iconoModal
                }
                style={{
                  margin: 0,
                  color:
                    colorModal,
                  fontSize: 17,
                }}
              />
            </div>

            <div>
              <div
                style={{
                  color:
                    "#172b4d",
                  fontSize: 17,
                  fontWeight:
                    700,
                }}
              >
                {tituloModal}
              </div>

              <div
                style={{
                  color:
                    "#8793a5",
                  fontSize: 12,
                  fontWeight:
                    400,
                  marginTop: 2,
                }}
              >
                Administración desde BackOffice
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Content>
          {/* MENSAJE */}

          {accionModal ===
            "CERRAR_SESION" && (
            <p>
              ¿Desea cerrar de forma
              forzada la sesión de este
              usuario?
            </p>
          )}

          {accionModal ===
            "BLOQUEAR" && (
            <>
              <p>
                ¿Desea bloquear el acceso
                de este usuario?
              </p>

              {sesionSeleccionada?.activa ===
                true && (
                <Message
                  warning
                  size="small"
                  style={{
                    boxShadow:
                      "none",
                  }}
                >
                  <Icon
                    name="warning sign"
                    color="orange"
                  />

                  El usuario tiene una sesión activa. Al bloquearlo, su sesión también será cerrada.
                </Message>
              )}
            </>
          )}

          {accionModal ===
            "DESBLOQUEAR" && (
            <p>
              ¿Desea habilitar nuevamente
              el acceso de este usuario?
            </p>
          )}

          {/* DATOS USUARIO */}

          <div
            style={{
              border:
                "1px solid #edf0f4",
              borderRadius: 7,
              overflow:
                "hidden",
              marginTop: 14,
            }}
          >
            <ModalFila
              icon="user outline"
              color="#2185d0"
              titulo="Usuario"
              valor={`${usuarioSeleccionado?.nombre || ""} ${
                usuarioSeleccionado?.apellido || ""
              }`.trim()}
            />

            <ModalFila
              icon="mail outline"
              color="#6435c9"
              titulo="Correo"
              valor={
                usuarioSeleccionado?.correo
              }
            />

            <ModalFila
              icon="building outline"
              color="#2185d0"
              titulo="Empresa"
              valor={
                empresasMap[
                  usuarioSeleccionado?.emp_id
                ] ||
                usuarioSeleccionado?.empresa
              }
            />

            <ModalFila
              icon="id badge outline"
              color="#0d9da5"
              titulo="Rol"
              valor={
                usuarioSeleccionado?.rol
              }
              ultimo
            />
          </div>
        </Modal.Content>

        <Modal.Actions
          style={{
            background:
              "#f8fafc",
            borderTop:
              "1px solid #e5e9ef",
          }}
        >
          <Button
            type="button"
            disabled={
              procesando
            }
            onClick={
              cerrarModal
            }
            style={{
              background:
                "#ffffff",
              color:
                "#526273",
              border:
                "1px solid #d7dee7",
              boxShadow:
                "none",
            }}
          >
            Cancelar
          </Button>

          <Button
            type="button"
            loading={
              procesando
            }
            disabled={
              procesando
            }
            onClick={
              ejecutarAccion
            }
            style={{
              background:
                colorModal,
              color:
                "#ffffff",
              border:
                `1px solid ${colorModal}`,
              boxShadow:
                "none",
            }}
          >
            {!procesando && (
              <Icon
                name={
                  iconoModal
                }
                style={{
                  color:
                    "#ffffff",
                }}
              />
            )}

            {accionModal ===
              "CERRAR_SESION" &&
              "Cerrar sesión"}

            {accionModal ===
              "BLOQUEAR" &&
              "Bloquear"}

            {accionModal ===
              "DESBLOQUEAR" &&
              "Desbloquear"}
          </Button>
        </Modal.Actions>
      </Modal>
    </Container>
  );
};

// ============================================================
// KPI CARD
// ============================================================

const ResumenCard = ({
  titulo,
  valor,
  descripcion,
  icon,
  colorIcono,
  fondoIcono,
}) => {
  return (
    <div
      style={{
        background:
          "#ffffff",
        border:
          "1px solid #e5e9ef",
        borderRadius: 8,
        padding:
          "15px 16px",
        boxShadow:
          "0 2px 6px rgba(0,0,0,0.04)",
        display: "flex",
        alignItems:
          "center",
        minHeight: 92,
      }}
    >
      <div
        style={{
          width: 46,
          height: 46,
          minWidth: 46,
          borderRadius: 12,
          background:
            fondoIcono,
          display: "flex",
          alignItems:
            "center",
          justifyContent:
            "center",
          marginRight: 14,
        }}
      >
        <Icon
          name={icon}
          style={{
            margin: 0,
            fontSize: 19,
            color:
              colorIcono,
          }}
        />
      </div>

      <div>
        <div
          style={{
            fontSize: 10,
            color:
              "#697586",
            fontWeight: 700,
            letterSpacing:
              "0.3px",
            marginBottom: 3,
          }}
        >
          {titulo}
        </div>

        <div
          style={{
            fontSize: 26,
            color:
              colorIcono,
            fontWeight: 700,
            lineHeight: 1.1,
          }}
        >
          {valor}
        </div>

        <div
          style={{
            color:
              "#8793a5",
            fontSize: 10,
            marginTop: 5,
          }}
        >
          {descripcion}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// FILA MODAL
// ============================================================

const ModalFila = ({
  icon,
  color,
  titulo,
  valor,
  ultimo = false,
}) => {
  return (
    <div
      style={{
        padding:
          "10px 13px",
        display: "flex",
        justifyContent:
          "space-between",
        alignItems:
          "center",
        gap: 15,
        borderBottom:
          ultimo
            ? "none"
            : "1px solid #edf0f4",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems:
            "center",
          gap: 7,
          color:
            "#697586",
          fontSize: 12,
        }}
      >
        <Icon
          name={icon}
          style={{
            color,
            margin: 0,
          }}
        />

        {titulo}
      </div>

      <strong
        style={{
          color:
            "#344563",
          fontSize: 12,
          textAlign:
            "right",
        }}
      >
        {valor || "-"}
      </strong>
    </div>
  );
};

// ============================================================
// TABLE HEADER
// ============================================================

const stickyTh = {
  position: "sticky",
  top: 0,
  zIndex: 2,
  background: "#f8fafc",
  color: "#344563",
  fontWeight: 600,
};

export default UsuariosGlobales;